import { Injectable, signal, computed } from '@angular/core';

// ─── Status ──────────────────────────────────────────────────────────────────
export type SnackbarStatus = 'loading' | 'done' | 'error';

// ─── CTA button config ───────────────────────────────────────────────────────
export interface SnackbarAction {
  /** Button label */
  label: string;
  /** Optional PrimeIcons class e.g. "pi pi-external-link" */
  icon?: string;
  /** Callback fired when the button is clicked */
  handler: () => void;
}

// ─── Notification descriptor ─────────────────────────────────────────────────
export interface SnackbarNotification {
  /** Status controls icon + accent colour */
  status: SnackbarStatus;
  /** Primary headline */
  title: string;
  /** Optional secondary line */
  subtitle?: string;
  /** Optional pill badge (e.g. vendor name, filename) */
  badge?: string;
  /** CTA button shown when status === 'done' or whenever provided */
  action?: SnackbarAction;
  /** Whether the × button is shown. Defaults true when status !== 'loading' */
  dismissible?: boolean;
}

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * GlobalSnackbarService
 *
 * A lightweight, singleton service for displaying a single persistent
 * status snackbar from anywhere in the app. Survives page navigation.
 *
 * Usage:
 * ```ts
 * private snackbar = inject(GlobalSnackbarService);
 *
 * // Show a loading snackbar
 * this.snackbar.show({
 *   status: 'loading',
 *   title: 'Uploading file…',
 *   subtitle: 'This may take a moment.',
 *   badge: 'invoice.pdf',
 * });
 *
 * // Update to done with a CTA
 * this.snackbar.update({
 *   status: 'done',
 *   title: 'Upload complete',
 *   action: { label: 'Open', icon: 'pi pi-external-link', handler: () => this.openRecord() },
 * });
 *
 * // Dismiss programmatically
 * this.snackbar.dismiss();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class GlobalSnackbarService {
  private readonly _notification = signal<SnackbarNotification | null>(null);

  /** The current notification — null means hidden */
  readonly notification = this._notification.asReadonly();

  /** True whenever a notification is active */
  readonly isVisible = computed(() => this._notification() !== null);

  /** True when status is 'loading' */
  readonly isLoading = computed(() => this._notification()?.status === 'loading');

  // ── API ──────────────────────────────────────────────────────────────────

  /**
   * Show (or replace) a snackbar notification.
   */
  show(notification: SnackbarNotification): void {
    this._notification.set({ ...notification });
  }

  /**
   * Partially update the current notification (e.g. status loading → done).
   * If no notification is active, this is a no-op.
   */
  update(patch: Partial<SnackbarNotification>): void {
    const current = this._notification();
    if (!current) return;
    this._notification.set({ ...current, ...patch });
  }

  /**
   * Dismiss and hide the snackbar.
   */
  dismiss(): void {
    this._notification.set(null);
  }
}
